import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { useAdminAuth } from '../../context/AdminAuthContext';
import {
  Loader2, Eye, MessageSquare, Clock, CheckCircle2, AlertCircle,
  XCircle, HelpCircle, ChevronLeft, ChevronRight, Inbox, Mail,
  AlertTriangle, User
} from 'lucide-react';

export const AdminTickets = () => {
  const { token } = useAdminAuth();
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');
  const [includeSpam, setIncludeSpam] = useState(false);
  const limit = 20;

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  // Fetch ticket stats
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await fetch(`${backendUrl}/api/admin/tickets/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setStats(data);
        }
      } catch (error) {
        console.error('Stats error:', error);
      }
    };
    fetchStats();
  }, [token, backendUrl]);

  // Fetch tickets
  useEffect(() => {
    const fetchTickets = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams({
          limit: limit.toString(),
          skip: (page * limit).toString(),
          include_spam: includeSpam.toString()
        });
        if (statusFilter !== 'all') {
          params.append('status', statusFilter);
        }

        const response = await fetch(`${backendUrl}/api/admin/tickets?${params}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setTickets(data.tickets);
          setTotal(data.total);
        }
      } catch (error) {
        console.error('Tickets error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTickets();
  }, [token, backendUrl, page, statusFilter, includeSpam]);

  const getStatusBadge = (status) => {
    const configs = {
      'open': { class: 'bg-blue-500/20 text-blue-400', icon: Inbox, label: 'Offen' },
      'in_progress': { class: 'bg-yellow-500/20 text-yellow-400', icon: Clock, label: 'In Bearbeitung' },
      'waiting_customer': { class: 'bg-purple-500/20 text-purple-400', icon: HelpCircle, label: 'Wartet auf Kunde' },
      'resolved': { class: 'bg-green-500/20 text-green-400', icon: CheckCircle2, label: 'Gelöst' },
      'closed': { class: 'bg-slate-500/20 text-slate-400', icon: XCircle, label: 'Geschlossen' },
    };
    return configs[status] || configs['open'];
  };

  const getPriorityBadge = (priority) => {
    const configs = {
      'low': { class: 'bg-slate-500/20 text-slate-400', label: 'Niedrig' },
      'normal': { class: 'bg-blue-500/20 text-blue-400', label: 'Normal' },
      'high': { class: 'bg-orange-500/20 text-orange-400', label: 'Hoch' },
      'urgent': { class: 'bg-red-500/20 text-red-400', label: 'Dringend' },
    };
    return configs[priority] || configs['normal'];
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="space-y-6" data-testid="admin-tickets">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Support Tickets</h1>
          <p className="text-slate-400 mt-1">{total} Tickets insgesamt</p>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card className="bg-blue-500/10 border-blue-500/30">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <Inbox className="h-8 w-8 text-blue-400" />
                <div>
                  <p className="text-2xl font-bold text-white">{stats.open}</p>
                  <p className="text-xs text-blue-300">Offen</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-yellow-500/10 border-yellow-500/30">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <Clock className="h-8 w-8 text-yellow-400" />
                <div>
                  <p className="text-2xl font-bold text-white">{stats.in_progress}</p>
                  <p className="text-xs text-yellow-300">In Bearbeitung</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-purple-500/10 border-purple-500/30">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <HelpCircle className="h-8 w-8 text-purple-400" />
                <div>
                  <p className="text-2xl font-bold text-white">{stats.waiting_customer}</p>
                  <p className="text-xs text-purple-300">Wartet</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-green-500/10 border-green-500/30">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="h-8 w-8 text-green-400" />
                <div>
                  <p className="text-2xl font-bold text-white">{stats.resolved}</p>
                  <p className="text-xs text-green-300">Gelöst</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-orange-500/10 border-orange-500/30">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <User className="h-8 w-8 text-orange-400" />
                <div>
                  <p className="text-2xl font-bold text-white">{stats.unassigned}</p>
                  <p className="text-xs text-orange-300">Nicht zugewiesen</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-red-500/10 border-red-500/30">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className="h-8 w-8 text-red-400" />
                <div>
                  <p className="text-2xl font-bold text-white">{stats.spam}</p>
                  <p className="text-xs text-red-300">Spam</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48 bg-slate-700 border-slate-600 text-white">
                <SelectValue placeholder="Status filtern" />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                <SelectItem value="all">Alle Status</SelectItem>
                <SelectItem value="open">Offen</SelectItem>
                <SelectItem value="in_progress">In Bearbeitung</SelectItem>
                <SelectItem value="waiting_customer">Wartet auf Kunde</SelectItem>
                <SelectItem value="resolved">Gelöst</SelectItem>
                <SelectItem value="closed">Geschlossen</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant={includeSpam ? "default" : "outline"}
              onClick={() => setIncludeSpam(!includeSpam)}
              className={includeSpam ? "bg-red-600 hover:bg-red-700" : "border-slate-600 text-slate-300"}
            >
              <AlertTriangle className="h-4 w-4 mr-2" />
              Spam {includeSpam ? 'anzeigen' : 'ausblenden'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tickets Table */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="pt-6">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          ) : tickets.length === 0 ? (
            <div className="text-center py-20">
              <Mail className="h-16 w-16 mx-auto text-slate-600 mb-4" />
              <p className="text-slate-500">Keine Tickets gefunden</p>
              <p className="text-slate-600 text-sm mt-2">
                Neue Tickets werden automatisch erstellt, wenn E-Mails an support@wolkenhelden.de gesendet werden.
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Ticket</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Betreff</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Kunde</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Status</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Priorität</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Zugewiesen</th>
                    <th className="text-left py-3 px-4 text-slate-400 font-medium">Aktualisiert</th>
                    <th className="text-right py-3 px-4 text-slate-400 font-medium">Aktion</th>
                  </tr>
                </thead>
                <tbody>
                  {tickets.map((ticket) => {
                    const statusConfig = getStatusBadge(ticket.status);
                    const priorityConfig = getPriorityBadge(ticket.priority);
                    const StatusIcon = statusConfig.icon;
                    return (
                      <tr 
                        key={ticket.id} 
                        className={`border-b border-slate-700/50 hover:bg-slate-700/30 ${ticket.is_spam ? 'opacity-60' : ''}`}
                      >
                        <td className="py-4 px-4">
                          <div className="flex items-center gap-2">
                            {ticket.is_spam && <AlertTriangle className="h-4 w-4 text-red-400" />}
                            <span className="text-blue-400 font-mono text-sm font-semibold">
                              {ticket.ticket_number}
                            </span>
                          </div>
                          <span className="text-slate-500 text-xs">
                            {ticket.message_count} Nachricht{ticket.message_count !== 1 ? 'en' : ''}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <span className="text-white line-clamp-1 max-w-xs">
                            {ticket.subject}
                          </span>
                        </td>
                        <td className="py-4 px-4">
                          <div>
                            <span className="text-white text-sm">{ticket.customer_name || 'Unbekannt'}</span>
                            <p className="text-slate-500 text-xs">{ticket.customer_email}</p>
                          </div>
                        </td>
                        <td className="py-4 px-4">
                          <Badge className={statusConfig.class}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            {statusConfig.label}
                          </Badge>
                        </td>
                        <td className="py-4 px-4">
                          <Badge className={priorityConfig.class}>
                            {priorityConfig.label}
                          </Badge>
                        </td>
                        <td className="py-4 px-4">
                          {ticket.assigned_name ? (
                            <span className="text-slate-300 text-sm">{ticket.assigned_name}</span>
                          ) : (
                            <span className="text-slate-500 text-sm italic">Nicht zugewiesen</span>
                          )}
                        </td>
                        <td className="py-4 px-4">
                          <span className="text-slate-400 text-sm">
                            {new Date(ticket.updated_at).toLocaleDateString('de-DE', {
                              day: '2-digit',
                              month: '2-digit',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </td>
                        <td className="py-4 px-4 text-right">
                          <Button asChild variant="ghost" size="sm" className="text-blue-400 hover:text-blue-300">
                            <Link to={`/admin/tickets/${ticket.id}`}>
                              <Eye className="h-4 w-4 mr-1" /> Öffnen
                            </Link>
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-700">
              <span className="text-slate-400 text-sm">
                Seite {page + 1} von {totalPages}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="border-slate-600 text-slate-300"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  className="border-slate-600 text-slate-300"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
